import { useState, useEffect } from 'react'
import styled, { css } from 'styled-components'

const NavbarWrapper = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${({ theme }) => theme.zIndex.sticky};
  background: ${({ theme, scrolled }) =>
    scrolled ? theme.colors.background : 'transparent'};
  backdrop-filter: ${({ scrolled }) => (scrolled ? 'blur(10px)' : 'none')};
  transition: all ${({ theme }) => theme.transitions.base};
  border-bottom: ${({ scrolled, theme }) =>
    scrolled ? `1px solid ${theme.colors.backgroundLighter}` : 'none'};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    background: ${({ theme }) => theme.colors.background};
  }
`

const NavContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`

const Logo = styled.a`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: ${({ theme }) => theme.fonts.sizes.xl};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};

  span {
    color: ${({ theme }) => theme.colors.primary};
  }
`

const NavLinks = styled.ul`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xl};
  align-items: center;
  list-style: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`

export const NavLink = styled.a`
  color: ${({ theme, active }) => (active ? theme.colors.primary : theme.colors.textSecondary)};
  font-weight: 500;
  transition: color ${({ theme }) => theme.transitions.fast};
  position: relative;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: ${({ active }) => (active ? '100%' : '0')};
    height: 2px;
    background: ${({ theme }) => theme.colors.primary};
    transition: width ${({ theme }) => theme.transitions.base};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary};

    &::after {
      width: 100%;
    }
  }
`

const MobileMenuButton = styled.button`
  display: none;
  flex-direction: column;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
  }
`

const HamburgerLine = styled.span`
  width: 24px;
  height: 2px;
  background: ${({ theme }) => theme.colors.text};
  transition: all ${({ theme }) => theme.transitions.base};
  transform-origin: center;

  ${({ open, index }) =>
    open &&
    css`
      &:nth-child(${index}) {
        transform: ${index === 1 ? 'rotate(45deg) translate(5px, 5px)' : 'rotate(-45deg) translate(5px, -5px)'};
      }
      &:nth-child(2) {
        opacity: 0;
      }
    `}
`

const MobileMenu = styled.div`
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.colors.backgroundLight};
  border-top: 1px solid ${({ theme }) => theme.colors.backgroundLighter};
  padding: ${({ theme }) => theme.spacing.lg} 0;
  transform: ${({ open }) => (open ? 'translateY(0)' : 'translateY(-100%)')};
  opacity: ${({ open }) => (open ? '1' : '0')};
  transition: all ${({ theme }) => theme.transitions.base};
  pointer-events: ${({ open }) => (open ? 'all' : 'none')};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: block;
  }

  ${NavLinks} {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.lg};
    padding: 0 ${({ theme }) => theme.spacing.md};
  }
`

const CTAButton = styled.a`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.gradientPrimary};
  color: ${({ theme }) => theme.colors.text};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: 600;
  transition: all ${({ theme }) => theme.transitions.base};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.glow};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`

const navigationLinks = [
  { id: 'home', label: 'Home', href: '#home' },
  { id: 'features', label: 'Features', href: '#features' },
  { id: 'trailer', label: 'Trailer', href: '#trailer' },
  { id: 'about', label: 'About', href: '#about' },
  { id: 'testimonials', label: 'Reviews', href: '#testimonials' },
]

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [activeSection, setActiveSection] = useState('home')

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50)

      // Determine active section based on scroll position
      const sections = navigationLinks.map(link => link.id)
      for (const sectionId of sections) {
        const element = document.getElementById(sectionId)
        if (element) {
          const rect = element.getBoundingClientRect()
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(sectionId)
            break
          }
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToSection = (e, href) => {
    e.preventDefault()
    const element = document.querySelector(href)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
      setMobileMenuOpen(false)
    }
  }

  return (
    <NavbarWrapper scrolled={scrolled}>
      <NavContainer>
        <Logo href="#home">Nexus<span>Realms</span></Logo>

        <NavLinks>
          {navigationLinks.map(link => (
            <li key={link.id}>
              <NavLink
                href={link.href}
                active={activeSection === link.id ? 1 : 0}
                onClick={(e) => scrollToSection(e, link.href)}
              >
                {link.label}
              </NavLink>
            </li>
          ))}
        </NavLinks>

        <CTAButton href="#preorder">Pre-Order Now</CTAButton>

        <MobileMenuButton
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
          aria-expanded={mobileMenuOpen}
        >
          <HamburgerLine open={mobileMenuOpen} index={1} />
          <HamburgerLine open={mobileMenuOpen} index={2} />
          <HamburgerLine open={mobileMenuOpen} index={3} />
        </MobileMenuButton>
      </NavContainer>

      <MobileMenu open={mobileMenuOpen}>
        <NavLinks>
          {navigationLinks.map(link => (
            <li key={link.id}>
              <NavLink
                href={link.href}
                active={activeSection === link.id ? 1 : 0}
                onClick={(e) => scrollToSection(e, link.href)}
              >
                {link.label}
              </NavLink>
            </li>
          ))}
        </NavLinks>
      </MobileMenu>
    </NavbarWrapper>
  )
}

export default Navbar
